import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../auth/AuthProvider';
import { useDoc } from '../lib/useFirestore';
import { CallStatus, useCallable } from '../lib/useCallable';
import { evaluateCondition } from '../lib/conditions';
import { APPLICATION_ADDABLE_TYPES, effectiveApplicationSections, REQUIREMENTS } from '../lib/formSections';
import { DEFAULT_PAGE_COPY, resolvePageCopy } from '../lib/applicationPageCopy';
import CandidateBanner from '../components/CandidateBanner';
import Field from '../components/Field';
import RichText from '../components/RichText';
import { draftNotice } from '../lib/privacyNotice';
import { PrivacyNotice, TickBox, openTable, widthClass } from './ApplicationForm';
import { usePageIdentity } from '../lib/usePageIdentity';

/**
 * The application for employment, as the applicant sees it — and, with Edit on, editable where it
 * stands.
 *
 * THE TEXT IS THE EDITOR. In Edit, every piece of wording on the form is itself an edit box that
 * looks exactly like the words it holds: the page's title, the box at the top, section titles and
 * introductions, questions, help text, statements, a dropdown's choices, a yes/no question's
 * answers, a table's columns and its add button, the section notes, the button, and what the
 * applicant is told once they have sent it. Click any of it and type. The first version put each
 * question's wording in a panel underneath it, behind an Edit button, so changing a word meant
 * finding the button, finding the box, and reading the result somewhere else; that is the thing
 * this replaces.
 *
 * The rest of a question's settings sit in a small bar that appears over it on hover: required or
 * optional, which section it is in, when it is shown, move up or down, hide (a shipped question)
 * or delete (one the firm added), and for an added question the kind of answer. Sections have the
 * same bar: move, hide, or delete one the firm added. "Add a question" sits at the end of every
 * section, "Add a section" under it. The wording that is not a question — the box at the top, a
 * section's introduction, the section notes, the line and button after submitting — has a Hide
 * of its own; hidden wording stays in the editor, faded, with Show.
 *
 * THE ONE THING THAT CANNOT CHANGE: a shipped question's type and path, because answers are stored
 * against both — so a shipped question is hidden, never deleted, and a shipped choice or column is
 * retired (gone from the form, still printed on a record that used it). Everything else is the
 * firm's, including the privacy notice's text, title and date, whether a self-identification
 * question is required, and the separately-filed sections themselves (hide, move, move questions
 * in or out). Those were locked until 1.107.0; the firm asked for all of it.
 *
 * The preview is built from the draft by lib/formSections.js, held to the server's output by a
 * test, and drawn with the applicant page's own components. Nothing reaches applicants until Save.
 * Form Editors save the wording keys only (the server enforces it); admins save the whole document,
 * so the recipients and sender configured on it ride along untouched.
 */

const plain = (value) => value && typeof value === 'object' && !Array.isArray(value);
const blank = (value) => typeof value === 'string' && !value.trim();
const REQUIREMENT_LABELS = { required: 'Required', soft_required: 'Ask why if blank', optional: 'Optional' };
const TYPE_LABELS = Object.fromEntries(APPLICATION_ADDABLE_TYPES);
const NOT_A_QUESTION = ['notice', 'ccpaNotice'];
// Questions whose answer another question can be shown after.
const CAN_TRIGGER = ['yesno', 'select', 'checkbox'];
// Every override key that is about the question itself rather than where it sits; "Original" clears these.
const QUESTION_KEYS = [
  'label',
  'helpText',
  'requirement',
  'text',
  'columns',
  'optionLabels',
  'addedOptions',
  'hiddenOptions',
  'addedColumns',
  'hiddenColumns',
  'yesLabel',
  'noLabel',
  'addLabel',
  'visibleIf',
  'sectionId',
  'title',
  'lastModified',
  'display',
  'columnRequired',
  'maxColumns',
  'width',
];
// Keys whose blank value means "as ships", so a stored blank is shown as what applicants get.
const BLANK_IS_DEFAULT = ['label', 'text', 'yesLabel', 'noLabel', 'addLabel', 'title', 'lastModified'];
// Written out whole so Tailwind sees them: an empty help box shows only while its question is
// hovered or being edited, an empty section introduction only while its section is.
// Invisible, not removed: a box taken out of the page while empty vanished in the instant between
// the mouse going down on it and it taking focus, so a click on "+ Add help text" landed nowhere.
const GHOST = {
  q: 'opacity-0 group-hover/q:opacity-100 group-focus-within/q:opacity-100 focus-within:opacity-100',
  sec: 'opacity-0 group-hover/sec:opacity-100 group-focus-within/sec:opacity-100 focus-within:opacity-100',
};

const chip =
  'rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-700 hover:border-firm-500 hover:text-firm-700 disabled:opacity-40 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
const mini =
  'rounded border border-slate-300 bg-white py-0.5 pl-1 pr-6 text-xs dark:border-slate-600 dark:bg-slate-800';

/** A short id for something the firm adds: a question, a section, a choice, a column. */
const newId = (prefix) => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

/** Drops undefined values, so "back to what ships" removes the key rather than storing a hole. */
const compact = (object) => Object.fromEntries(Object.entries(object).filter(([, v]) => v !== undefined));

/**
 * The editable part of the settings document, tidied.
 *
 * A blank that means "as ships" is dropped rather than kept: the applicant is served what ships
 * for it, and an editor showing an empty box where the applicant sees words was the bug that
 * made the voluntary note look missing. (A blank help line or section introduction is different:
 * it is a deliberate "show nothing here", and stays.)
 */
function fromSettings(settings) {
  const source = plain(settings) ? settings : {};
  const overrides = {};
  for (const [path, raw] of Object.entries(plain(source.overrides) ? source.overrides : {})) {
    if (!plain(raw)) continue;
    const next = Object.fromEntries(
      Object.entries(raw).filter(([key, value]) => !(BLANK_IS_DEFAULT.includes(key) && blank(value)))
    );
    if (Object.keys(next).length) overrides[path] = next;
  }
  const sectionOverrides = {};
  for (const [id, raw] of Object.entries(plain(source.sectionOverrides) ? source.sectionOverrides : {})) {
    if (!plain(raw)) continue;
    const next = Object.fromEntries(Object.entries(raw).filter(([key, value]) => !(key === 'title' && blank(value))));
    if (Object.keys(next).length) sectionOverrides[id] = next;
  }
  const wording = (raw) =>
    Object.fromEntries(
      Object.entries(plain(raw) ? raw : {}).filter(([key, value]) =>
        key === 'hidden' ? Array.isArray(value) && value.length > 0 : typeof value === 'string' && value.trim()
      )
    );
  const pageCopy = wording(source.pageCopy);
  const emailCopy = wording(source.emailCopy);
  return {
    overrides,
    customFields: plain(source.customFields) ? source.customFields : {},
    sectionOverrides,
    customSections: plain(source.customSections) ? source.customSections : {},
    pageCopy,
    emailCopy,
  };
}

/** A draft value if one was typed, otherwise what ships — so clearing a box leaves it empty to type into. */
const typed = (draftValue, shippedValue) => (draftValue !== undefined ? draftValue : shippedValue || '');

/** A section the firm added is keyed without the `custom.` its id carries on the form. */
const customSectionKey = (sectionId) => String(sectionId).replace(/^custom\./, '');

export default function ApplicationFormEditor() {
  const { isFormEditor, isAdmin } = useAuth();
  // The same tab as the Applications page it is opened from, not the intake app's.
  usePageIdentity({ title: 'Applications for Employment — AALRR', icon: '/ccc-favicon.svg' });
  const { data: settings, loading } = useDoc('settings/employmentApplication');
  const { run, busy, error, saved } = useCallable();
  const [shipped, setShipped] = useState(null);
  const [noticeDefaults, setNoticeDefaults] = useState(null);
  const [emailDefaults, setEmailDefaults] = useState(null);
  const [hideableEmail, setHideableEmail] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(true);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    let cancelled = false;
    httpsCallable(functions, 'getApplicationSchema')({})
      .then((result) => {
        if (cancelled) return;
        setNoticeDefaults(result.data.noticeDefaults || null);
        setEmailDefaults(result.data.emailCopyDefaults || null);
        setHideableEmail(result.data.hideableEmailCopy || []);
        setShipped(result.data.shipped || null);
      })
      .catch((err) => !cancelled && setLoadError(err.message || String(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  const stored = useMemo(() => fromSettings(settings), [settings]);
  useEffect(() => {
    if (!loading && draft === null) setDraft(stored);
  }, [loading, stored, draft]);

  const dirty = draft !== null && JSON.stringify(fromSettings(draft)) !== JSON.stringify(stored);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // A question, choice or column the firm added, with its wording cleared mid-edit, would otherwise
  // drop out of the form (a question with no wording is not a question) and take its edit box with it.
  const forPreview = useMemo(() => {
    if (!draft) return draft;
    const named = (list, fallback) =>
      Array.isArray(list) ? list.map((item) => (plain(item) && !String(item.label || '').trim() ? { ...item, label: fallback } : item)) : list;
    const customFields = Object.fromEntries(
      Object.entries(draft.customFields).map(([id, raw]) => [
        id,
        plain(raw)
          ? {
              ...raw,
              label: String(raw.label || '').trim() ? raw.label : 'Untitled question',
              options: named(raw.options, 'Untitled choice'),
              columns: named(raw.columns, 'Untitled column'),
            }
          : raw,
      ])
    );
    const overrides = Object.fromEntries(
      Object.entries(draft.overrides).map(([path, raw]) => [
        path,
        plain(raw)
          ? { ...raw, addedOptions: named(raw.addedOptions, 'Untitled choice'), addedColumns: named(raw.addedColumns, 'Untitled column') }
          : raw,
      ])
    );
    return { ...draft, customFields, overrides };
  }, [draft]);
  const sections = useMemo(
    () => (shipped && forPreview ? effectiveApplicationSections(shipped, forPreview, { includeHidden: editing }) : []),
    [shipped, forPreview, editing]
  );
  const shippedByPath = useMemo(
    () => Object.fromEntries((shipped || []).flatMap((s) => s.fields || []).map((f) => [f.path, f])),
    [shipped]
  );
  const shippedSectionOf = useMemo(
    () =>
      Object.fromEntries((shipped || []).flatMap((s) => (s.fields || []).map((f) => [f.path, s]))),
    [shipped]
  );
  const untitled = draft
    ? Object.values(draft.customFields).filter((raw) => plain(raw) && !String(raw.label || '').trim()).length
    : 0;

  if (!isFormEditor) {
    return (
      <Shell>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Editing the application needs the Form Editor role. An administrator can add it on Admin → Roster.
        </p>
      </Shell>
    );
  }
  if (loadError) {
    return (
      <Shell>
        <p className="text-sm text-red-600 dark:text-red-400">The form could not be loaded: {loadError}</p>
      </Shell>
    );
  }
  if (!shipped || !draft) {
    return (
      <Shell>
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading the form…</p>
      </Shell>
    );
  }

  // What applicants get (hidden wording empty), and the words themselves for the edit boxes.
  const pageCopy = resolvePageCopy(draft.pageCopy);
  const words = resolvePageCopy({ ...draft.pageCopy, hidden: [] });
  const hiddenCopy = Array.isArray(draft.pageCopy.hidden) ? draft.pageCopy.hidden : [];
  const isHidden = (key) => hiddenCopy.includes(key);
  // Sections a question may be moved to (any), and the questions another can be shown after.
  const movable = sections;
  // The privacy notice as the draft has it, for the preview and the edit boxes' defaults.
  const noticeOverride = draft.overrides['privacyNotice.document'] || {};
  const notice = noticeDefaults ? draftNotice(noticeOverride, noticeDefaults) : null;
  const triggers = sections.flatMap((s) =>
    s.fields.filter((f) => CAN_TRIGGER.includes(f.type)).map((f) => ({ ...f, sectionTitle: s.title }))
  );

  // ── edits ────────────────────────────────────────────────────────────────

  const setOverride = (path, patch) =>
    setDraft((prev) => {
      const next = compact({ ...(prev.overrides[path] || {}), ...patch });
      const overrides = { ...prev.overrides };
      if (Object.keys(next).length) overrides[path] = next;
      else delete overrides[path];
      return { ...prev, overrides };
    });
  const setSection = (id, patch) =>
    setDraft((prev) => {
      const next = compact({ ...(prev.sectionOverrides[id] || {}), ...patch });
      const sectionOverrides = { ...prev.sectionOverrides };
      if (Object.keys(next).length) sectionOverrides[id] = next;
      else delete sectionOverrides[id];
      return { ...prev, sectionOverrides };
    });
  const setCustomSection = (id, patch) =>
    setDraft((prev) => {
      const key = customSectionKey(id);
      return { ...prev, customSections: { ...prev.customSections, [key]: { ...prev.customSections[key], ...patch } } };
    });
  const setCustom = (id, patch) =>
    setDraft((prev) => ({ ...prev, customFields: { ...prev.customFields, [id]: { ...prev.customFields[id], ...patch } } }));
  const setPage = (key, value) => setDraft((prev) => ({ ...prev, pageCopy: { ...prev.pageCopy, [key]: value } }));
  const setEmail = (key, value) => setDraft((prev) => ({ ...prev, emailCopy: { ...prev.emailCopy, [key]: value } }));
  const toggleEmail = (key) =>
    setDraft((prev) => {
      const list = Array.isArray(prev.emailCopy.hidden) ? prev.emailCopy.hidden : [];
      const hidden = list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
      const next = { ...prev.emailCopy, hidden };
      if (!hidden.length) delete next.hidden;
      return { ...prev, emailCopy: next };
    });
  const toggleCopy = (key) =>
    setDraft((prev) => {
      const list = Array.isArray(prev.pageCopy.hidden) ? prev.pageCopy.hidden : [];
      const hidden = list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
      const next = { ...prev.pageCopy, hidden };
      if (!hidden.length) delete next.hidden;
      return { ...prev, pageCopy: next };
    });

  const move = (section, path, step) =>
    setDraft((prev) => {
      const list = section.fields.map((f) => f.path);
      const at = list.indexOf(path);
      const to = at + step;
      if (at < 0 || to < 0 || to >= list.length) return prev;
      [list[at], list[to]] = [list[to], list[at]];
      const overrides = { ...prev.overrides };
      list.forEach((p, index) => {
        overrides[p] = { ...(overrides[p] || {}), order: index };
      });
      return { ...prev, overrides };
    });

  // Sections move up and down among all of them, the separately-filed ones included.
  const moveSection = (id, step) =>
    setDraft((prev) => {
      const list = movable.map((s) => s.id);
      const at = list.indexOf(id);
      const to = at + step;
      if (at < 0 || to < 0 || to >= list.length) return prev;
      [list[at], list[to]] = [list[to], list[at]];
      const sectionOverrides = { ...prev.sectionOverrides };
      list.forEach((s, index) => {
        sectionOverrides[s] = { ...(sectionOverrides[s] || {}), order: index };
      });
      return { ...prev, sectionOverrides };
    });

  const addSection = (after) => {
    const key = newId('s');
    const id = `custom.${key}`;
    setDraft((prev) => {
      const list = movable.map((s) => s.id);
      list.splice(list.indexOf(after.id) + 1, 0, id);
      const sectionOverrides = { ...prev.sectionOverrides };
      list.forEach((s, index) => {
        sectionOverrides[s] = { ...(sectionOverrides[s] || {}), order: index };
      });
      return { ...prev, customSections: { ...prev.customSections, [key]: { title: '' } }, sectionOverrides };
    });
    setTimeout(() => document.getElementById(`section-${id}`)?.focus(), 50);
  };

  const removeSection = (id) =>
    setDraft((prev) => {
      const customSections = { ...prev.customSections };
      delete customSections[customSectionKey(id)];
      const sectionOverrides = { ...prev.sectionOverrides };
      delete sectionOverrides[id];
      return { ...prev, customSections, sectionOverrides };
    });

  const addQuestion = (section) => {
    const id = newId('q');
    setDraft((prev) => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [id]: { label: '', type: 'text', requirement: 'optional', sectionId: section.id },
      },
      overrides: { ...prev.overrides, [`custom.${id}`]: { order: section.fields.length } },
    }));
    // Straight into the new question's wording, which is the first thing anybody types.
    setTimeout(() => document.getElementById(`label-custom.${id}`)?.focus(), 50);
  };

  const removeCustom = (path) =>
    setDraft((prev) => {
      const id = path.replace(/^custom\./, '');
      const customFields = { ...prev.customFields };
      delete customFields[id];
      const overrides = { ...prev.overrides };
      delete overrides[path];
      return { ...prev, customFields, overrides };
    });

  // To another section: last in it, and for a question the firm added, its own home changes.
  const moveTo = (field, sectionId) => {
    const target = sections.find((s) => s.id === sectionId);
    const order = target ? target.fields.length : 0;
    if (field.custom) {
      setCustom(field.path.replace(/^custom\./, ''), { sectionId });
      setOverride(field.path, { order });
    } else {
      const home = shippedSectionOf[field.path];
      setOverride(field.path, { sectionId: home && home.id === sectionId ? undefined : sectionId, order });
    }
  };

  const save = async () => {
    if (untitled) return;
    const tidy = fromSettings(draft);
    // `id` is the hook's, not the document's; spreading it back in would write it as a field.
    const { id: _ignored, ...document } = settings || {};
    const value = isAdmin ? { ...document, ...tidy } : tidy;
    await run('adminUpdateSettings', { docId: 'employmentApplication', value });
    setDraft(tidy);
  };

  const sectionOverride = (id) => draft.sectionOverrides[id] || {};

  // ── page ─────────────────────────────────────────────────────────────────

  return (
    <Shell>
      <div className="sticky top-0 z-30 -mx-4 mb-4 flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-100/95 px-4 py-3 backdrop-blur dark:border-slate-700 dark:bg-slate-950/95">
        <div className="inline-flex overflow-hidden rounded border border-slate-300 dark:border-slate-600">
          {[
            [true, 'Edit'],
            [false, 'Preview'],
          ].map(([value, label]) => (
            <button
              key={label}
              type="button"
              onClick={() => setEditing(value)}
              className={`px-4 py-1.5 text-sm font-medium ${
                editing === value
                  ? 'bg-firm-700 text-white dark:bg-firm-500'
                  : 'bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {editing
            ? 'Click any words on the form and type. Hover a question or section for its other settings.'
            : 'Exactly what an applicant sees. Try it — nothing here is sent.'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {untitled > 0 && (
            <span className="text-xs text-red-600 dark:text-red-400">A question you added has no wording yet.</span>
          )}
          {dirty && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-900 dark:text-amber-100">
              Unsaved changes
            </span>
          )}
          <CallStatus busy={busy} error={error} saved={saved && !dirty} />
          <button
            type="button"
            disabled={!dirty || busy}
            onClick={() => setDraft(stored)}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
          >
            Discard
          </button>
          <button
            type="button"
            disabled={!dirty || busy || untitled > 0}
            onClick={save}
            className="rounded bg-firm-700 px-4 py-1.5 text-sm font-medium text-white disabled:opacity-40 dark:bg-firm-500"
          >
            Save changes
          </button>
        </div>
      </div>

      {/* The applicant's page, framed as they get it. */}
      <div className="overflow-hidden rounded-lg border border-slate-300 shadow-sm dark:border-slate-700">
        <CandidateBanner
          title={
            editing ? (
              <InlineText
                onDark
                value={typed(draft.pageCopy.title, DEFAULT_PAGE_COPY.title)}
                placeholder={DEFAULT_PAGE_COPY.title}
                onChange={(v) => setPage('title', v)}
              />
            ) : (
              pageCopy.title
            )
          }
          width="max-w-5xl"
          logo
        />
        <div className="bg-page px-4 py-8 dark:bg-slate-950">
          <div className="mx-auto max-w-5xl">
            {editing ? (
              <Hideable hidden={isHidden('intro')} onToggle={() => toggleCopy('intro')}>
                <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  <InlineText
                    multiline
                    value={typed(draft.pageCopy.intro, DEFAULT_PAGE_COPY.intro)}
                    placeholder={DEFAULT_PAGE_COPY.intro}
                    onChange={(v) => setPage('intro', v)}
                    hint="**bold**, a new line, or a blank line between paragraphs. Empty goes back to the original."
                  />
                </div>
              </Hideable>
            ) : (
              pageCopy.intro && (
                <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  <RichText>{pageCopy.intro}</RichText>
                </div>
              )
            )}

            {sections.map((section) => {
              const original = (shipped || []).find((s) => s.id === section.id) || {};
              const customSection = section.custom === true;
              const ownSection = customSection ? draft.customSections[customSectionKey(section.id)] || {} : null;
              const fields = editing
                ? section.fields
                : section.fields.filter((f) => f.visibleIf === undefined || evaluateCondition(f.visibleIf, answers));
              const at = movable.findIndex((s) => s.id === section.id);
              const noteKey = section.separateDocument === 'privacyNotice' ? 'privacyNote' : 'voluntaryNote';
              return (
                <section
                  key={section.id}
                  className={`group/sec relative mt-6 ${editing ? '-mx-2 rounded-md px-2 py-1 hover:bg-white/40 dark:hover:bg-slate-900/40' : ''} ${
                    section.hidden ? 'opacity-50' : ''
                  }`}
                >
                  {editing ? (
                    <>
                      {(
                        <div className="absolute -top-3 right-2 z-20 hidden items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 shadow-sm group-hover/sec:flex dark:border-slate-700 dark:bg-slate-900">
                          <span className="pr-1 text-[11px] text-slate-500">Section</span>
                          <button type="button" className={chip} disabled={at <= 0} onClick={() => moveSection(section.id, -1)} title="Move this section up">
                            ↑
                          </button>
                          <button
                            type="button"
                            className={chip}
                            disabled={at < 0 || at >= movable.length - 1}
                            onClick={() => moveSection(section.id, 1)}
                            title="Move this section down"
                          >
                            ↓
                          </button>
                          {customSection ? (
                            <button
                              type="button"
                              className={chip}
                              disabled={section.fields.length > 0}
                              title={section.fields.length ? 'Move or delete its questions first' : 'Delete this section'}
                              onClick={() => removeSection(section.id)}
                            >
                              Delete
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={chip}
                              onClick={() => setSection(section.id, { hidden: section.hidden ? undefined : true })}
                            >
                              {section.hidden ? 'Show' : 'Hide'}
                            </button>
                          )}
                        </div>
                      )}
                      {(section.hidden || customSection) && (
                        <div className="mb-1 flex flex-wrap gap-1.5">
                          {section.hidden && <Badge tone="slate">Hidden from applicants, with all its questions</Badge>}
                          {customSection && <Badge tone="firm">Added by the firm</Badge>}
                        </div>
                      )}
                      <InlineText
                        id={`section-${section.id}`}
                        className="text-base font-semibold text-ink dark:text-slate-100"
                        value={customSection ? ownSection.title || '' : typed(sectionOverride(section.id).title, original.title)}
                        placeholder={customSection ? 'Name this section' : original.title}
                        onChange={(v) =>
                          customSection ? setCustomSection(section.id, { title: v }) : setSection(section.id, { title: v })
                        }
                      />
                      {!customSection && original.description && sectionOverride(section.id).description === '' ? (
                        // Hidden: the paragraph that ships is kept, just not shown, so it comes back whole.
                        <Hideable hidden onToggle={() => setSection(section.id, { description: undefined })}>
                          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{original.description}</p>
                        </Hideable>
                      ) : (
                        <Hideable
                          hidden={false}
                          offer={!customSection && Boolean(original.description)}
                          onToggle={() => setSection(section.id, { description: '' })}
                        >
                          <InlineText
                            multiline
                            className="mt-1 text-sm text-slate-600 dark:text-slate-300"
                            value={
                              customSection
                                ? ownSection.description || ''
                                : typed(sectionOverride(section.id).description, original.description)
                            }
                            placeholder="+ Add an introduction to this section"
                            ghost="sec"
                            onChange={(v) =>
                              customSection
                                ? setCustomSection(section.id, { description: v })
                                : setSection(section.id, { description: v })
                            }
                          />
                        </Hideable>
                      )}
                    </>
                  ) : (
                    <>
                      <h2 className="text-base font-semibold text-ink dark:text-slate-100">{section.title}</h2>
                      {section.description && (
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{section.description}</p>
                      )}
                    </>
                  )}

                  {section.separateDocument &&
                    (editing ? (
                      <Hideable hidden={isHidden(noteKey)} onToggle={() => toggleCopy(noteKey)}>
                        <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          <InlineText
                            multiline
                            value={typed(draft.pageCopy[noteKey], words[noteKey])}
                            placeholder={words[noteKey]}
                            onChange={(v) => setPage(noteKey, v)}
                          />
                        </div>
                      </Hideable>
                    ) : (
                      pageCopy[noteKey] && (
                        <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          <RichText>{pageCopy[noteKey]}</RichText>
                        </div>
                      )
                    ))}

                  <div className="mt-3 grid grid-cols-6 gap-x-4 gap-y-4">
                    {fields.map((field, index) =>
                      editing ? (
                        <div key={field.path} className={widthClass(field)}>
                        <EditableQuestion
                          field={field}
                          shipped={shippedByPath[field.path]}
                          rawOverride={draft.overrides[field.path] || {}}
                          rawCustom={draft.customFields[field.path.replace(/^custom\./, '')] || {}}
                          sectionId={section.id}
                          canMove
                          destinations={sections}
                          notice={notice}
                          noticeDefaults={noticeDefaults}
                          triggers={triggers.filter((t) => t.path !== field.path)}
                          first={index === 0}
                          last={index === fields.length - 1}
                          onMove={(step) => move(section, field.path, step)}
                          onMoveTo={(id) => moveTo(field, id)}
                          onOverride={(patch) => setOverride(field.path, patch)}
                          onCustom={(patch) => setCustom(field.path.replace(/^custom\./, ''), patch)}
                          onRemove={() => removeCustom(field.path)}
                        />
                        </div>
                      ) : (
                        <div key={field.path} className={widthClass(field)}>
                          <ApplicantView field={field} answers={answers} onChange={setAnswers} notice={notice} />
                        </div>
                      )
                    )}
                  </div>

                  {editing && (
                    <button
                      type="button"
                      onClick={() => addQuestion(section)}
                      className="mt-3 w-full rounded border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500 hover:border-firm-500 hover:text-firm-700 dark:border-slate-600 dark:text-slate-400"
                    >
                      + Add a question here
                    </button>
                  )}
                  {editing && (
                    <button
                      type="button"
                      onClick={() => addSection(section)}
                      className="mt-2 w-full rounded px-3 py-1 text-xs text-slate-400 opacity-0 hover:text-firm-700 group-hover/sec:opacity-100 focus:opacity-100 dark:text-slate-500"
                    >
                      + Add a section after this one
                    </button>
                  )}
                </section>
              );
            })}

            <div className="mt-8 border-t border-slate-200 py-4 dark:border-slate-700">
              {editing ? (
                <span className="inline-block min-w-[14rem] rounded bg-firm-700 px-5 py-2.5 dark:bg-firm-500">
                  <InlineText
                    onDark
                    className="text-sm font-medium text-white"
                    value={typed(draft.pageCopy.submitLabel, DEFAULT_PAGE_COPY.submitLabel)}
                    placeholder={DEFAULT_PAGE_COPY.submitLabel}
                    onChange={(v) => setPage('submitLabel', v)}
                  />
                </span>
              ) : (
                <span className="inline-block rounded bg-firm-700 px-5 py-2.5 text-sm font-medium text-white dark:bg-firm-500">
                  {pageCopy.submitLabel}
                </span>
              )}
              {editing ? (
                <span className="ml-3 inline-block min-w-[12rem] rounded border border-slate-300 px-4 py-2 align-top dark:border-slate-600">
                  <InlineText
                    className="text-sm text-slate-700 dark:text-slate-200"
                    value={typed(draft.pageCopy.saveLabel, DEFAULT_PAGE_COPY.saveLabel)}
                    placeholder={DEFAULT_PAGE_COPY.saveLabel}
                    onChange={(v) => setPage('saveLabel', v)}
                  />
                </span>
              ) : (
                <span className="ml-3 inline-block rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 dark:border-slate-600 dark:text-slate-200">
                  {pageCopy.saveLabel}
                </span>
              )}
              {editing ? (
                <span className="ml-3 inline-block min-w-[9rem] align-top">
                  <Hideable hidden={isHidden('previewLabel')} onToggle={() => toggleCopy('previewLabel')}>
                    <InlineText
                      className="text-sm text-firm-700 underline dark:text-firm-300"
                      value={typed(draft.pageCopy.previewLabel, DEFAULT_PAGE_COPY.previewLabel)}
                      placeholder={DEFAULT_PAGE_COPY.previewLabel}
                      onChange={(v) => setPage('previewLabel', v)}
                    />
                  </Hideable>
                </span>
              ) : (
                pageCopy.previewLabel && <span className="ml-3 text-sm text-firm-700 underline dark:text-firm-300">{pageCopy.previewLabel}</span>
              )}
            </div>

            {/* What the applicant sees once they have sent it. */}
            {editing && (
              <div className="mt-4">
                <p className="mb-1 text-xs text-slate-500 dark:text-slate-400">After the applicant submits, they see:</p>
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-100">
                  <InlineText
                    className="font-medium"
                    value={typed(draft.pageCopy.thankYou, DEFAULT_PAGE_COPY.thankYou)}
                    placeholder={DEFAULT_PAGE_COPY.thankYou}
                    onChange={(v) => setPage('thankYou', v)}
                  />
                  <Hideable hidden={isHidden('thankYouDetail')} onToggle={() => toggleCopy('thankYouDetail')}>
                    <InlineText
                      className="mt-1"
                      value={typed(draft.pageCopy.thankYouDetail, DEFAULT_PAGE_COPY.thankYouDetail)}
                      placeholder={DEFAULT_PAGE_COPY.thankYouDetail}
                      onChange={(v) => setPage('thankYouDetail', v)}
                    />
                  </Hideable>
                  <Hideable hidden={isHidden('downloadLabel')} onToggle={() => toggleCopy('downloadLabel')}>
                    <span className="mt-3 inline-block min-w-[12rem] rounded bg-firm-700 px-4 py-2 dark:bg-firm-500">
                      <InlineText
                        onDark
                        className="text-sm font-medium text-white"
                        value={typed(draft.pageCopy.downloadLabel, DEFAULT_PAGE_COPY.downloadLabel)}
                        placeholder={DEFAULT_PAGE_COPY.downloadLabel}
                        onChange={(v) => setPage('downloadLabel', v)}
                      />
                    </span>
                  </Hideable>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && (
        <WordingPanel
          title="Messages applicants may see"
          intro="Shown only when they apply: while saving or sending, when something is missing, when a link has stopped working. Words in {braces} are filled in."
          rows={PAGE_MESSAGES}
          values={draft.pageCopy}
          defaults={DEFAULT_PAGE_COPY}
          hiddenKeys={hiddenCopy}
          hideable={['savedLabel']}
          onChange={setPage}
          onToggle={toggleCopy}
        />
      )}
      {editing && emailDefaults && (
        <>
          <EmailMock
            title="The invitation email, to the applicant"
            note="Sent only when “Email the link” is ticked on the Applications page."
            heading="inviteHeading"
            button="inviteButton"
            body={['inviteGreeting', 'inviteIntro', 'inviteSaveNote']}
            footer={['inviteFooter', 'inviteExpiry']}
            subject="inviteSubject"
            values={draft.emailCopy}
            defaults={emailDefaults}
            hideable={hideableEmail}
            onChange={setEmail}
            onToggle={toggleEmail}
          />
          <EmailMock
            title="The completed-application email, to HR"
            note="The privacy-notice and self-identification lines are sent only when that document is attached."
            heading="hrHeading"
            button="hrButton"
            body={['hrIntro', 'hrApplicationAttached', 'hrPrivacyAttached', 'hrSelfIdAttached', 'hrNoSelfId', 'hrSubmittedFrom']}
            footer={['hrFooter']}
            subject="hrSubject"
            values={draft.emailCopy}
            defaults={emailDefaults}
            hideable={hideableEmail}
            onChange={setEmail}
            onToggle={toggleEmail}
          />
        </>
      )}
    </Shell>
  );
}

/** The page's messages, in the order an applicant might meet them. */
const PAGE_MESSAGES = [
  ['tabTitle', 'Browser tab'],
  ['savingLabel', 'Save button, while saving'],
  ['savedLabel', 'After saving ({time})'],
  ['sendingLabel', 'Submit button, while sending'],
  ['attentionOne', 'Heading when one answer is missing'],
  ['attentionMany', 'Heading when several are ({count})'],
  ['requiredMessage', 'A required question left blank ({question})'],
  ['usuallyProvidedMessage', 'An “if none, say why” question left blank ({question})'],
  ['tableRequiredMessage', 'A required table with no entries ({section})'],
  ['columnRequiredMessage', 'A required box in a table entry ({question}, {row}, {column})'],
  ['mustAgreeMessage', 'A box that must be ticked ({section})'],
  ['reasonPrompt', 'Above the “why there is none” box'],
  ['clearChoice', 'Under a listed-out question, to clear it'],
  ['notGiven', 'A filled-in-for-you answer that is empty'],
  ['noticeLoading', 'While the privacy notice loads'],
  ['submitFailed', 'Sending failed'],
  ['alreadySubmitted', 'Already submitted'],
  ['linkIncomplete', 'Link: incomplete'],
  ['linkInvalid', 'Link: not valid'],
  ['linkExpired', 'Link: expired'],
  ['linkMismatch', 'Link: for a different application'],
  ['linkReplaced', 'Link: replaced by a newer one'],
  ['linkSwitchedOff', 'Link: switched off'],
  ['linkNotFound', 'Link: application not found'],
  ['linkFailed', 'Link: any other problem'],
];

/** A list of labelled wording boxes; blank goes back to the default. */
function WordingPanel({ title, intro, rows, values, defaults, hiddenKeys = [], hideable = [], onChange, onToggle }) {
  return (
    <div className="mt-6 rounded-lg border border-slate-300 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <h2 className="text-sm font-semibold text-ink dark:text-slate-100">{title}</h2>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{intro} Empty goes back to the original.</p>
      <div className="mt-3 space-y-2">
        {rows.map(([key, label]) => {
          const box = (
            <div className="rounded border border-slate-200 px-1.5 text-sm text-ink focus-within:border-firm-500 dark:border-slate-700 dark:text-slate-100">
              <InlineText multiline value={typed(values[key], defaults[key])} placeholder={defaults[key]} onChange={(v) => onChange(key, v)} />
            </div>
          );
          return (
            <div key={key} className="grid gap-1 sm:grid-cols-[14rem_1fr] sm:gap-3">
              <span className="pt-1 text-xs text-slate-500 dark:text-slate-400">{label}</span>
              {hideable.includes(key) ? (
                <Hideable hidden={hiddenKeys.includes(key)} onToggle={() => onToggle(key)}>
                  {box}
                </Hideable>
              ) : (
                box
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One of the application's emails, laid out as it arrives, every line editable in place. Lines
 * the firm may leave out have Hide. {name}, {position}, {email} and {days} are filled in.
 */
function EmailMock({ title, note, subject, heading, body, button, footer, values, defaults, hideable, onChange, onToggle }) {
  const box = (key, className = '', onDark = false) => {
    const text = (
      <InlineText
        multiline={!onDark}
        onDark={onDark}
        className={className}
        value={typed(values[key], defaults[key])}
        placeholder={defaults[key]}
        onChange={(v) => onChange(key, v)}
      />
    );
    const hidden = Array.isArray(values.hidden) && values.hidden.includes(key);
    return hideable.includes(key) ? (
      <Hideable key={key} hidden={hidden} onToggle={() => onToggle(key)}>
        {text}
      </Hideable>
    ) : (
      <div key={key}>{text}</div>
    );
  };
  return (
    <div className="mt-6">
      <h2 className="text-sm font-semibold text-ink dark:text-slate-100">{title}</h2>
      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
        {note} {'{name}'}, {'{position}'}, {'{email}'} and {'{days}'} are filled in; **bold** works. Empty goes back to the original.
      </p>
      <div className="mt-2 overflow-hidden rounded-lg border border-slate-300 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start gap-2 border-b border-slate-200 px-4 py-2 text-sm dark:border-slate-700">
          <span className="pt-0.5 text-xs text-slate-500">Subject:</span>
          <div className="min-w-0 flex-1">{box(subject, 'font-medium text-ink dark:text-slate-100')}</div>
        </div>
        <div className="bg-[#1F3864] px-4 py-3">
          {box(heading, 'text-base font-semibold text-white', true)}
          {box('firmName', 'text-xs text-[#c9d3e8]', true)}
        </div>
        <div className="space-y-2 px-4 py-4 text-sm text-ink dark:text-slate-100">
          {body.map((key) => box(key))}
          <span className="inline-block min-w-[14rem] bg-[#1F3864] px-5 py-2.5">{box(button, 'text-sm font-bold text-white', true)}</span>
          {box('buttonFallback', 'text-xs text-slate-500')}
        </div>
        <div className="space-y-1 px-4 pb-4 text-xs text-slate-500">{footer.map((key) => box(key, 'text-xs'))}</div>
      </div>
    </div>
  );
}

/** One question as the applicant gets it. */
function ApplicantView({ field, answers, onChange, disabled = false, notice }) {
  if (field.type === 'ccpaNotice') return <PrivacyNotice draft={notice || undefined} />;
  if (field.type === 'checkbox') return <TickBox field={field} answers={answers} onChange={onChange} disabled={disabled} />;
  if (field.locked === true) {
    return (
      <div className="rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
        <div className="text-xs text-slate-500 dark:text-slate-400">{field.label}</div>
        <div className="text-[15px] text-slate-400 dark:text-slate-500">Filled in from the invitation</div>
      </div>
    );
  }
  return (
    <Field
      field={field.helpText ? { ...openTable(field), help: <RichText>{field.helpText}</RichText> } : openTable(field)}
      matter={answers}
      config={{}}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

/** The editor's own frame: the internal banner, and the way back. */
function Shell({ children }) {
  return (
    <main className="min-h-full bg-slate-100 dark:bg-slate-950">
      <CandidateBanner title="Application form" width="max-w-6xl" showVersion showSignOut />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <Link to="/apply" className="text-sm text-firm-700 underline dark:text-firm-200">
          ← Applications
        </Link>
        <div className="mt-4">{children}</div>
      </div>
    </main>
  );
}

/**
 * Wording that can be taken off the page: a Hide button over it on hover, and while hidden it stays
 * in the editor, faded, labelled, with Show. `offer={false}` draws it without the button.
 */
function Hideable({ hidden, onToggle, offer = true, children }) {
  return (
    <div className={`group/hide relative ${hidden ? 'opacity-50' : ''}`}>
      {hidden && (
        <div className="mb-1 mt-2">
          <Badge tone="slate">Hidden from applicants</Badge>
        </div>
      )}
      {offer && (
        <button
          type="button"
          className={`${chip} absolute -top-2 right-1 z-10 ${hidden ? '' : 'hidden group-hover/hide:block group-focus-within/hide:block'}`}
          onClick={onToggle}
        >
          {hidden ? 'Show' : 'Hide'}
        </button>
      )}
      {children}
    </div>
  );
}

function Badge({ tone, children }) {
  const tones = {
    slate: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    firm: 'bg-firm-100 text-firm-800 dark:bg-firm-900 dark:text-firm-100',
    sky: 'bg-sky-100 text-sky-900 dark:bg-sky-900 dark:text-sky-100',
  };
  return <span className={`rounded px-1.5 py-0.5 text-[11px] ${tones[tone]}`}>{children}</span>;
}

/**
 * Words that are their own edit box: a borderless, self-sizing textarea in the same type as the text
 * it replaces, which shows it can be edited on hover and when focused. Single-line ones end at Enter.
 */
function InlineText({ value, onChange, placeholder, className = '', multiline = false, hint, id, onDark = false, ghost }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    // A `ghost` box (help text, a section introduction) is only an offer to add something, so while
    // it is empty it shows only when its question or section is hovered or being edited; forty
    // "+ Add help text" lines down the form were noise.
    <div className={`group/text ${ghost && !String(value || '').trim() ? GHOST[ghost] : ''}`}>
      <textarea
        ref={ref}
        id={id}
        rows={1}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (!multiline && e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        // min-h: a box measured while hidden (an empty help line before its question is hovered)
        // comes out zero pixels tall; one line is the least any edit box may be.
        className={`-mx-1 block min-h-[1.6em] w-[calc(100%+0.5rem)] resize-none overflow-hidden rounded border-0 bg-transparent px-1 py-0.5 outline-none ring-0 placeholder:italic ${
          onDark
            ? 'placeholder:text-white/60 hover:bg-white/10 focus:bg-white/15 focus:ring-2 focus:ring-white'
            : 'placeholder:text-slate-400 hover:bg-amber-50 hover:ring-1 hover:ring-amber-300 focus:bg-white focus:ring-2 focus:ring-firm-500 dark:hover:bg-slate-800 dark:focus:bg-slate-900'
        } ${className}`}
      />
      {hint && (
        <p className="mt-1 hidden text-[11px] text-slate-500 group-focus-within/text:block dark:text-slate-400">{hint}</p>
      )}
    </div>
  );
}

/** A question in edit mode: its words editable in place, its control shown, its settings on hover. */
function EditableQuestion({
  field,
  shipped,
  rawOverride,
  rawCustom,
  sectionId,
  canMove,
  destinations,
  notice,
  noticeDefaults,
  triggers,
  first,
  last,
  onMove,
  onMoveTo,
  onOverride,
  onCustom,
  onRemove,
}) {
  const custom = field.custom === true;
  const question = !NOT_A_QUESTION.includes(field.type);
  const kind = custom ? rawCustom.type || 'text' : field.type;
  const set = (patch) => (custom ? onCustom(patch) : onOverride(patch));
  const changed = !custom && shipped && QUESTION_KEYS.some((key) => rawOverride[key] !== undefined);
  const [showWhenOpen, setShowWhenOpen] = useState(false);

  const changeKind = (type) => {
    // A new table starts with one column to name, rather than as a table nobody can fill in.
    const columns = rawCustom.columns && rawCustom.columns.length ? rawCustom.columns : [{ path: newId('c'), label: 'Column 1' }];
    onCustom(type === 'repeatable' ? { type, columns } : { type });
  };

  return (
    <div
      className={`group/q relative -m-2 rounded-md border border-transparent p-2 hover:border-slate-300 focus-within:border-slate-300 dark:hover:border-slate-600 ${
        field.hidden ? 'opacity-50' : ''
      }`}
    >
      {/* Settings, over the question's top-right corner: shown on hover, and while it is being edited. */}
      {/* A narrow question's bar wraps onto several lines, so it opens upward from the question's
          top edge instead of down over its own wording. */}
      <div
        className={`absolute ${
          field.width === 'half' || field.width === 'third' ? 'bottom-full left-0' : '-top-3'
        } right-2 z-20 hidden flex-wrap items-center justify-end gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 shadow-sm group-hover/q:flex group-focus-within/q:flex dark:border-slate-700 dark:bg-slate-900`}
      >
        {question && (
          <select
            className={mini}
            value={field.requirement || 'optional'}
            title="What the applicant must do"
            onChange={(e) => set({ requirement: e.target.value })}
          >
            {REQUIREMENTS.map((r) => (
              <option key={r} value={r}>
                {REQUIREMENT_LABELS[r]}
              </option>
            ))}
          </select>
        )}
        <select
          className={mini}
          value={field.width || 'full'}
          title="How much of the line this question takes on a wide screen (a phone always shows it full width)"
          onChange={(e) => onOverride({ width: e.target.value === ((shipped && shipped.width) || 'full') ? undefined : e.target.value })}
        >
          <option value="full">Full width</option>
          <option value="half">Half width</option>
          <option value="third">Third width</option>
        </select>
        {field.type === 'repeatable' && !field.stacked && (
          <select
            className={mini}
            value={field.maxColumns || 4}
            title="How many boxes of each entry sit on one line, on a wide enough screen"
            onChange={(e) => {
              const n = Number(e.target.value);
              onOverride({ maxColumns: n === ((shipped && shipped.maxColumns) || 4) ? undefined : n });
            }}
          >
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? 'box' : 'boxes'} per line
              </option>
            ))}
          </select>
        )}
        {field.type === 'select' && (
          <select
            className={mini}
            value={field.display === 'list' ? 'list' : 'dropdown'}
            title="How the choices are shown"
            // In the override for both kinds of question; back to how it ships is no override.
            onChange={(e) =>
              onOverride({
                display: e.target.value === ((shipped && shipped.display) || 'dropdown') ? undefined : e.target.value,
              })
            }
          >
            <option value="dropdown">Choices in a dropdown</option>
            <option value="list">Choices listed out</option>
          </select>
        )}
        {custom && (
          <select className={mini} value={kind} title="Kind of answer" onChange={(e) => changeKind(e.target.value)}>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        )}
        {canMove && (
          <select
            className={`${mini} max-w-[10rem]`}
            value={sectionId}
            title="Which section this is in"
            onChange={(e) => onMoveTo(e.target.value)}
          >
            {destinations.map((s) => (
              <option key={s.id} value={s.id}>
                In: {s.title}
              </option>
            ))}
          </select>
        )}
        {question && (
          <button type="button" className={chip} onClick={() => setShowWhenOpen(!showWhenOpen)} title="When this question is shown">
            Show when…
          </button>
        )}
        <button type="button" className={chip} disabled={first} onClick={() => onMove(-1)} title="Move up">
          ↑
        </button>
        <button type="button" className={chip} disabled={last} onClick={() => onMove(1)} title="Move down">
          ↓
        </button>
        {custom ? (
          <button type="button" className={chip} onClick={onRemove}>
            Delete
          </button>
        ) : (
          <button type="button" className={chip} onClick={() => onOverride({ hidden: field.hidden ? undefined : true })}>
            {field.hidden ? 'Show' : 'Hide'}
          </button>
        )}
        {changed && (
          <button
            type="button"
            className={chip}
            title="Undo every change to this question"
            onClick={() => onOverride(Object.fromEntries(QUESTION_KEYS.map((key) => [key, undefined])))}
          >
            Original
          </button>
        )}
      </div>

      {(field.hidden || custom || field.visibleIf !== undefined) && (
        <div className="mb-1 flex flex-wrap gap-1.5">
          {field.hidden && <Badge tone="slate">Hidden from applicants</Badge>}
          {custom && <Badge tone="firm">Added by the firm</Badge>}
          {field.visibleIf !== undefined && (
            <button type="button" onClick={() => setShowWhenOpen(!showWhenOpen)} title="Change when this is shown">
              <Badge tone="sky">Shown only when {describeCondition(field.visibleIf, triggers)}</Badge>
            </button>
          )}
        </div>
      )}

      {showWhenOpen && question && (
        <ShowWhen
          condition={field.visibleIf}
          triggers={triggers}
          // null is "always" in place of a condition the question ships with; for any other
          // question, always is simply no condition.
          onChange={(visibleIf) =>
            onOverride({ visibleIf: visibleIf === null && !(shipped && shipped.visibleIf) ? undefined : visibleIf })
          }
          onClose={() => setShowWhenOpen(false)}
        />
      )}

      {field.type === 'ccpaNotice' ? (
        notice && noticeDefaults ? (
          <NoticeEditor override={rawOverride} notice={notice} defaults={noticeDefaults} onOverride={onOverride} />
        ) : (
          <PrivacyNotice />
        )
      ) : field.type === 'notice' ? (
        <div className="rounded-md border border-slate-200 bg-white p-4 text-sm font-medium text-ink dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
          <InlineText
            multiline
            value={custom ? rawCustom.text || '' : typed(rawOverride.text, shipped && shipped.text)}
            placeholder={shipped && shipped.text}
            onChange={(v) => onOverride({ text: v })}
          />
        </div>
      ) : (
        <>
          <div className="flex items-start gap-1">
            <div className="min-w-0 flex-1">
              <InlineText
                id={`label-${field.path}`}
                multiline={field.type === 'checkbox'}
                className="text-sm font-medium text-slate-700 dark:text-slate-200"
                value={custom ? rawCustom.label || '' : typed(rawOverride.label, shipped && shipped.label)}
                placeholder={custom ? 'Type the question' : shipped && shipped.label}
                onChange={(v) => set({ label: v })}
              />
            </div>
            {field.requirement === 'required' && <span className="pt-0.5 text-sm text-red-600">*</span>}
          </div>

          {field.type === 'yesno' ? (
            <YesNoLabels field={field} rawOverride={rawOverride} onOverride={onOverride} />
          ) : field.type !== 'select' && field.type !== 'repeatable' ? (
            <div className="pointer-events-none mt-1 select-none">
              <ControlOnly field={field} />
            </div>
          ) : null}

          {field.type === 'select' && !custom && (
            <ChoiceList
              title="Choices"
              current={field.options || []}
              retired={field.retiredOptions || []}
              originals={Object.fromEntries(((shipped && shipped.options) || []).map((o) => [o.value, o.label]))}
              onRename={(value, label) => {
                const shippedLabel = ((shipped && shipped.options) || []).find((o) => o.value === value);
                if (shippedLabel) {
                  const next = { ...(rawOverride.optionLabels || {}) };
                  if (!label.trim() || label === shippedLabel.label) delete next[value];
                  else next[value] = label;
                  onOverride({ optionLabels: Object.keys(next).length ? next : undefined });
                } else {
                  onOverride({
                    addedOptions: (rawOverride.addedOptions || []).map((o) => (o.value === value ? { ...o, label } : o)),
                  });
                }
              }}
              onRemove={(value) => onOverride({ hiddenOptions: [...(rawOverride.hiddenOptions || []), value] })}
              onRestore={(value) => {
                const next = (rawOverride.hiddenOptions || []).filter((v) => v !== value);
                onOverride({ hiddenOptions: next.length ? next : undefined });
              }}
              raw={Object.fromEntries((rawOverride.addedOptions || []).map((o) => [o.value, o.label || '']))}
              onAdd={() => onOverride({ addedOptions: [...(rawOverride.addedOptions || []), { value: newId('c'), label: 'New choice' }] })}
              addLabel="+ Add a choice"
            />
          )}
          {custom && kind === 'select' && (
            <ChoiceList
              title="Choices"
              current={rawCustom.options || []}
              retired={[]}
              originals={{}}
              onRename={(value, label) =>
                onCustom({ options: (rawCustom.options || []).map((o) => (o.value === value ? { ...o, label } : o)) })
              }
              raw={Object.fromEntries((rawCustom.options || []).map((o) => [o.value, o.label || '']))}
              onRemove={(value) => onCustom({ options: (rawCustom.options || []).filter((o) => o.value !== value) })}
              onAdd={() => onCustom({ options: [...(rawCustom.options || []), { value: newId('c'), label: 'New choice' }] })}
              addLabel="+ Add a choice"
            />
          )}

          {field.type === 'repeatable' && !custom && (
            <>
              <ChoiceList
                title="Columns"
                current={(field.fields || []).map((sub) => ({ value: sub.path, label: sub.label }))}
                retired={(field.retiredFields || []).map((sub) => ({ value: sub.path, label: sub.label }))}
                originals={Object.fromEntries(((shipped && shipped.fields) || []).map((sub) => [sub.path, sub.label]))}
                onRename={(path, label) => {
                  const original = ((shipped && shipped.fields) || []).find((sub) => sub.path === path);
                  if (original) {
                    const next = { ...(rawOverride.columns || {}) };
                    if (!label.trim() || label === original.label) delete next[path];
                    else next[path] = label;
                    onOverride({ columns: Object.keys(next).length ? next : undefined });
                  } else {
                    onOverride({
                      addedColumns: (rawOverride.addedColumns || []).map((c) => (c.path === path ? { ...c, label } : c)),
                    });
                  }
                }}
                raw={Object.fromEntries((rawOverride.addedColumns || []).map((c) => [c.path, c.label || '']))}
                onRemove={(path) => onOverride({ hiddenColumns: [...(rawOverride.hiddenColumns || []), path] })}
                onRestore={(path) => {
                  const next = (rawOverride.hiddenColumns || []).filter((p) => p !== path);
                  onOverride({ hiddenColumns: next.length ? next : undefined });
                }}
                onAdd={() =>
                  onOverride({
                    addedColumns: [...(rawOverride.addedColumns || []), { path: newId('c'), label: 'New column', type: 'text' }],
                  })
                }
                addLabel="+ Add a column"
                isRequired={(path) => columnIsRequired(field, path)}
                onRequired={(path, on) => onOverride({ columnRequired: nextColumnRequired(rawOverride, path, on) })}
              />
              <AddButtonLabel
                value={typed(rawOverride.addLabel, shipped && shipped.addLabel)}
                placeholder={shipped && shipped.addLabel}
                onChange={(v) => onOverride({ addLabel: v })}
              />
            </>
          )}
          {custom && kind === 'repeatable' && (
            <>
              <ChoiceList
                title="Columns"
                current={(rawCustom.columns || []).map((c) => ({ value: c.path, label: c.label }))}
                retired={[]}
                originals={{}}
                onRename={(path, label) =>
                  onCustom({ columns: (rawCustom.columns || []).map((c) => (c.path === path ? { ...c, label } : c)) })
                }
                raw={Object.fromEntries((rawCustom.columns || []).map((c) => [c.path, c.label || '']))}
                onRemove={(path) => onCustom({ columns: (rawCustom.columns || []).filter((c) => c.path !== path) })}
                onAdd={() => onCustom({ columns: [...(rawCustom.columns || []), { path: newId('c'), label: 'New column' }] })}
                addLabel="+ Add a column"
                isRequired={(path) => columnIsRequired(field, path)}
                onRequired={(path, on) => onOverride({ columnRequired: nextColumnRequired(rawOverride, path, on) })}
              />
              <AddButtonLabel
                value={rawCustom.addLabel || ''}
                placeholder="Add a row"
                onChange={(v) => onCustom({ addLabel: v })}
              />
            </>
          )}

          <InlineText
            multiline
            className="mt-1 text-xs text-slate-500 dark:text-slate-400"
            value={custom ? rawCustom.helpText || '' : typed(rawOverride.helpText, shipped && shipped.helpText)}
            placeholder="+ Add help text under this question"
            ghost="q"
            onChange={(v) => set({ helpText: v })}
          />
        </>
      )}
    </div>
  );
}

/**
 * The privacy notice, editable where it stands: its title, its text (one paragraph per line, "## "
 * for a heading, "- " for a bullet), and the Last modified line, which the signed PDF prints to say
 * which version was agreed to — so change it with the words. Empty goes back to the notice as it
 * ships.
 */
function NoticeEditor({ override, notice, defaults, onOverride }) {
  return (
    <div className="rounded-md border border-slate-300 p-4 text-xs leading-relaxed text-slate-700 dark:border-slate-600 dark:text-slate-200">
      <InlineText
        multiline
        className="text-sm font-semibold text-ink dark:text-slate-100"
        value={typed(override.title, defaults.title)}
        placeholder={defaults.title}
        onChange={(v) => onOverride({ title: v })}
      />
      <div className="mt-2">
        <InlineText
          multiline
          value={typed(override.text, defaults.text)}
          placeholder="The notice's text"
          onChange={(v) => onOverride({ text: v })}
          hint="One paragraph per line. Start a line with ## for a heading, or - for a bullet. Empty goes back to the original."
        />
      </div>
      <div className="mt-3 flex items-center gap-1 italic">
        <span>Last modified:</span>
        <div className="min-w-[10rem] flex-1">
          <InlineText
            value={typed(override.lastModified, defaults.lastModified)}
            placeholder={defaults.lastModified}
            onChange={(v) => onOverride({ lastModified: v })}
          />
        </div>
      </div>
      <p className="mt-2 text-[11px] not-italic text-slate-500">
        {notice.blocks.length} paragraphs, headings and bullets. Applicants see it formatted, in a scrolling box.
      </p>
    </div>
  );
}

/** The control alone, without Field's own label: the label above is the editable one. */
function ControlOnly({ field }) {
  return <Field field={{ ...field, label: '', requirement: undefined, help: undefined }} matter={{}} config={{}} onChange={() => {}} disabled />;
}

/** A yes/no question's two answers, each its own edit box in the shape the applicant clicks. */
function YesNoLabels({ field, rawOverride, onOverride }) {
  return (
    <div className="mt-1 flex items-center gap-3">
      {[
        ['yesLabel', 'Yes'],
        ['noLabel', 'No'],
      ].map(([key, shipped]) => (
        <div
          key={key}
          className="flex h-[46px] flex-1 items-center gap-2.5 rounded-md border border-slate-200 px-4 text-[15px] text-ink dark:border-slate-600 dark:text-slate-300"
        >
          <span aria-hidden="true" className="h-[17px] w-[17px] shrink-0 rounded-full border-[1.5px] border-slate-300 dark:border-slate-500" />
          <div className="min-w-0 flex-1">
            <InlineText
              value={typed(rawOverride[key], field[key] || shipped)}
              placeholder={shipped}
              onChange={(v) => onOverride({ [key]: v === shipped ? undefined : v })}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A table's "+ Add a …" button, its words editable. */
function AddButtonLabel({ value, placeholder, onChange }) {
  return (
    <div className="mt-2 inline-flex items-center gap-1 rounded border border-dashed border-slate-400 px-3 py-1 text-xs text-slate-600 dark:border-slate-500 dark:text-slate-300">
      <span>+</span>
      <div className="min-w-[8rem]">
        <InlineText value={value} placeholder={placeholder} onChange={onChange} />
      </div>
    </div>
  );
}

/**
 * A dropdown's choices or a table's columns: one full-width edit box each, so a long choice (the
 * self-identification categories run to a sentence) reads whole, a Remove beside each, and an Add at
 * the end. A shipped one that is removed is retired, listed underneath with Restore; long lists
 * (the fifty states) fold away until opened.
 */
function ChoiceList({ title, current, retired, originals, raw = {}, onRename, onRemove, onRestore, onAdd, addLabel, isRequired, onRequired }) {
  const [open, setOpen] = useState(current.length <= 8);
  const renamed = current.filter((item) => originals[item.value] !== undefined && originals[item.value] !== item.label).length;
  const added = current.filter((item) => originals[item.value] === undefined).length;
  const summary = [
    `${current.length}`,
    renamed ? `${renamed} renamed` : '',
    added && Object.keys(originals).length ? `${added} added` : '',
    retired.length ? `${retired.length} removed` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="mt-2 rounded border border-dashed border-slate-300 bg-slate-50/70 p-2 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-300">
      <div className="flex items-center gap-2">
        <span className="font-medium">{title}</span>
        <span className="text-slate-400">{summary}</span>
        {current.length > 8 && (
          <button type="button" className={`${chip} ml-auto`} onClick={() => setOpen(!open)}>
            {open ? 'Fold away' : 'Show all'}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-1.5 space-y-1">
          {current.map((item) => (
            <div key={item.value} className="flex items-start gap-1.5">
              <div className="min-w-0 flex-1 rounded border border-slate-300 bg-white text-sm text-ink focus-within:border-firm-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
                <InlineText
                  className="px-1.5"
                  value={raw[item.value] !== undefined ? raw[item.value] : item.label}
                  placeholder={originals[item.value] || 'Type it here'}
                  onChange={(v) => onRename(item.value, v)}
                />
              </div>
              {onRequired && (
                // A table's column: whether every entry must fill it in.
                <label className="mt-1 inline-flex items-center gap-1 whitespace-nowrap rounded border border-slate-300 bg-white px-1.5 py-0.5 dark:border-slate-600 dark:bg-slate-800">
                  <input
                    type="checkbox"
                    className="h-3 w-3 accent-firm-700"
                    checked={isRequired(item.value)}
                    onChange={(e) => onRequired(item.value, e.target.checked)}
                  />
                  Required
                </label>
              )}
              <button
                type="button"
                className={`${chip} mt-1`}
                disabled={current.length <= 1}
                title={current.length <= 1 ? 'There has to be at least one' : `Remove “${item.label}”`}
                onClick={() => onRemove(item.value)}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className={chip} onClick={onAdd}>
            {addLabel}
          </button>
        </div>
      )}
      {retired.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-slate-500">
          <span>Removed:</span>
          {retired.map((item) => (
            <span key={item.value} className="inline-flex items-center gap-1 rounded bg-slate-200 px-1.5 py-0.5 dark:bg-slate-700">
              <s>{item.label}</s>
              <button type="button" className="text-firm-700 underline dark:text-firm-200" onClick={() => onRestore(item.value)}>
                Restore
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Whether a table's column must be filled in on every entry, as the draft has it. */
const columnIsRequired = (field, path) =>
  ((field.fields || []).find((sub) => sub.path === path) || {}).requirement === 'required';

/** The table's `columnRequired` with one column switched; nothing left means the key goes. */
function nextColumnRequired(rawOverride, path, on) {
  const next = { ...(rawOverride.columnRequired || {}) };
  if (on) next[path] = true;
  else delete next[path];
  return Object.keys(next).length ? next : undefined;
}

/**
 * A choice list whose wording wraps, in place of a <select>, for choices too long for one line.
 * The closed box shows the choice in full; the open list scrolls inside the editor's width.
 */
function WrappingPicker({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (event) => ref.current && !ref.current.contains(event.target) && setOpen(false);
    const escape = (event) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  const current = options.find((o) => o.value === value) || options[0];
  const line = (o) => (
    <>
      <span className="break-words">{o.label}</span>
      {o.detail && <span className="ml-1 text-slate-500 dark:text-slate-400">({o.detail})</span>}
    </>
  );
  return (
    <div ref={ref} className="relative w-full min-w-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-start gap-2 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-left text-xs text-slate-800 hover:border-firm-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      >
        <span className="min-w-0 flex-1 whitespace-normal">{current ? line(current) : ''}</span>
        <span className="shrink-0 text-slate-400">▾</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 z-40 mt-1 max-h-72 overflow-y-auto rounded border border-slate-300 bg-white py-1 text-xs shadow-lg dark:border-slate-600 dark:bg-slate-900"
        >
          {options.map((o) => (
            <li key={o.value || '_none'}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`block w-full whitespace-normal px-2 py-1.5 text-left hover:bg-firm-50 dark:hover:bg-slate-800 ${
                  o.value === value ? 'bg-firm-50 font-medium dark:bg-slate-800' : ''
                }`}
              >
                {line(o)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** A condition, in words: "Have you applied before? is Yes". */
function describeCondition(condition, triggers) {
  if (!condition) return '';
  const trigger = triggers.find((t) => t.path === condition.path);
  const name = trigger ? `“${trigger.label}”` : 'another answer';
  if (condition.op === 'is_true') return `${name} is ${trigger && trigger.type === 'checkbox' ? 'ticked' : (trigger && trigger.yesLabel) || 'Yes'}`;
  if (condition.op === 'is_false') return `${name} is ${(trigger && trigger.noLabel) || 'No'}`;
  if (condition.op === 'neq' && typeof condition.value === 'boolean') {
    const word = condition.value ? (trigger && trigger.type === 'checkbox' ? 'ticked' : (trigger && trigger.yesLabel) || 'Yes') : (trigger && trigger.noLabel) || 'No';
    return `${name} is not ${word}`;
  }
  const option = trigger && (trigger.options || []).find((o) => o.value === condition.value);
  const value = option ? `“${option.label}”` : `“${condition.value}”`;
  return condition.op === 'neq' ? `${name} is not ${value}` : `${name} is ${value}`;
}

/**
 * When a question is shown: always, or only when another question was answered a certain way. A
 * single comparison is all the server accepts (forms/effectiveSections.js → cleanCondition).
 */
function ShowWhen({ condition, triggers, onChange, onClose }) {
  const trigger = condition && triggers.find((t) => t.path === condition.path);
  const pick = (path) => {
    const next = triggers.find((t) => t.path === path);
    if (!next) return onChange(null);
    if (next.type === 'select') return onChange({ path, op: 'eq', value: (next.options || [])[0]?.value || '' });
    return onChange({ path, op: 'is_true' });
  };
  return (
    <div className="mb-2 flex min-w-0 flex-wrap items-center gap-1.5 rounded border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100">
      <span>Show this</span>
      {/* Not a <select>: a browser's dropdown cannot wrap, and a question here can be a paragraph. */}
      <WrappingPicker
        value={condition ? condition.path : ''}
        options={[
          { value: '', label: 'always' },
          ...triggers.map((t) => ({ value: t.path, label: `when “${t.label}”`, detail: t.sectionTitle })),
        ]}
        onChange={pick}
      />
      {condition && trigger && trigger.type === 'select' && (
        <>
          <select className={mini} value={condition.op} onChange={(e) => onChange({ ...condition, op: e.target.value })}>
            <option value="eq">is</option>
            <option value="neq">is not</option>
          </select>
          <WrappingPicker
            value={condition.value}
            options={(trigger.options || []).map((o) => ({ value: o.value, label: o.label }))}
            onChange={(value) => onChange({ ...condition, value })}
          />
        </>
      )}
      {condition && trigger && trigger.type !== 'select' && (
        <select
          className={mini}
          value={condition.op === 'neq' ? `neq:${condition.value}` : condition.op}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v.startsWith('neq:') ? { path: condition.path, op: 'neq', value: v === 'neq:true' } : { path: condition.path, op: v });
          }}
        >
          <option value="is_true">{trigger.type === 'checkbox' ? 'is ticked' : `is ${trigger.yesLabel || 'Yes'}`}</option>
          {trigger.type !== 'checkbox' && <option value="is_false">is {trigger.noLabel || 'No'}</option>}
          {/* "Is not Yes" includes not answered at all, which "is No" does not. */}
          <option value="neq:true">{trigger.type === 'checkbox' ? 'is not ticked' : `is not ${trigger.yesLabel || 'Yes'} (or not answered)`}</option>
          {trigger.type !== 'checkbox' && <option value="neq:false">is not {trigger.noLabel || 'No'} (or not answered)</option>}
        </select>
      )}
      {condition && !trigger && <span>(the question it depended on is no longer on the form)</span>}
      <button type="button" className={`${chip} ml-auto`} onClick={onClose}>
        Done
      </button>
    </div>
  );
}
